import {LogsType, OcppMessageType} from 'src/types';

export type LogTypeSemantic = 'status' | 'transaction' | 'command' | 'error' | 'info' | 'other';

const TYPE_TO_SEMANTIC: Record<OcppMessageType, LogTypeSemantic> = {
    AUTHORIZE: 'info',
    BOOT_NOTIFICATION: 'status',
    CONNECTOR_STATUS_NOTIFICATION: 'status',
    DIAGNOSTIC_STATUS_NOTIFICATION: 'info',
    FIRMWARE_STATUS_NOTIFICATION: 'info',
    HEARTBEAT: 'info',
    METER_VALUES: 'transaction',
    START_TRANSACTION: 'transaction',
    STOP_TRANSACTION: 'transaction',
    DATA_TRANSFER: 'info',
    RESPONSE_STATUS: 'info',
    WS_CONNECTED: 'status',
    WS_DISCONNECTED: 'error',
    RFID_STATUS: 'info',
    CHANGE_CONFIGURATION: 'command',
    GET_CONFIGURATION: 'command',
    REMOTE_START_TRANSACTION: 'command',
    REMOTE_STOP_TRANSACTION: 'command',
    RESET: 'command',
    SET_CHARGING_PROFILE: 'command',
    TRIGGER_MESSAGE: 'command',
    UPDATE_FIRMWARE: 'command',
    UNLOCK_CONNECTOR: 'command',
    OTHER: 'other',
};

const SEMANTIC_COLORS: Record<LogTypeSemantic, { bg: string; text: string }> = {
    status: {bg: 'rgba(33, 150, 243, 0.12)', text: '#0d47a1'},
    transaction: {bg: 'rgba(76, 175, 80, 0.12)', text: '#1b5e20'},
    command: {bg: 'rgba(156, 39, 176, 0.12)', text: '#4a148c'},
    error: {bg: 'rgba(244, 67, 54, 0.12)', text: '#b71c1c'},
    info: {bg: 'rgba(96, 125, 139, 0.12)', text: '#37474f'},
    other: {bg: 'rgba(158, 158, 158, 0.12)', text: '#424242'},
};

export const getLogTypeSemantic = (type: OcppMessageType): LogTypeSemantic =>
    TYPE_TO_SEMANTIC[type] ?? 'other';

export const getLogTypeColors = (type: OcppMessageType) => SEMANTIC_COLORS[getLogTypeSemantic(type)];

export const getDirectionLabel = (direction: LogsType): string =>
    direction === 'CHARGER_OUT' ? 'fromCharger' : 'toCharger';

export interface ParsedPayload {
    messageTypeId?: number;
    uniqueId?: string;
    action?: string;
    body?: unknown;
    isValid: boolean;
    raw: string;
}

export const parseOcppPayload = (payload: string): ParsedPayload => {
    try {
        const parsed = JSON.parse(payload);
        if (Array.isArray(parsed) && parsed.length >= 2) {
            const [messageTypeId, uniqueId, actionOrBody, body] = parsed;
            const isResult = parsed.length === 3;
            return {
                messageTypeId: typeof messageTypeId === 'number' ? messageTypeId : undefined,
                uniqueId: typeof uniqueId === 'string' ? uniqueId : undefined,
                action: isResult ? undefined : (typeof actionOrBody === 'string' ? actionOrBody : undefined),
                body: isResult ? actionOrBody : body,
                isValid: true,
                raw: payload,
            };
        }
        return {isValid: true, body: parsed, raw: payload};
    } catch {
        return {isValid: false, raw: payload};
    }
};

export const formatPayloadPreview = (payload: string, maxLength = 100): string => {
    const parsed = parseOcppPayload(payload);
    if (!parsed.isValid) {
        return payload.length > maxLength ? `${payload.slice(0, maxLength)}…` : payload;
    }
    const bodyStr = parsed.body !== undefined ? JSON.stringify(parsed.body) : '';
    return bodyStr.length > maxLength ? `${bodyStr.slice(0, maxLength)}…` : bodyStr;
};

export const prettyPrintPayload = (payload: string): string => {
    try {
        return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
        return payload;
    }
};

export const humanizeOcppType = (action: string | undefined, fallback: string): string => {
    const src = action && action.trim() ? action : fallback;
    return src
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};
